/**
 * PhoneInput – Wrapper de PhoneInputWithCountrySelect con normalización
 * argentina incorporada.
 *
 * Uso:
 *   import PhoneInput from '@tools/components/PhoneInput';
 *   <PhoneInput defaultCountry="AR" value={val} onChange={setVal} />
 */
import React, { useCallback, useEffect, useRef } from 'react';
import PhoneInputWithCountrySelect from 'react-phone-number-input/min';

/**
 * Normaliza celulares argentinos: si el número empieza con +54 debe
 * llevar un 9 justo después del código de país (prefijo móvil obligatorio).
 *
 * Ejemplos:
 *   +5411...   → +54911...   (011-xxxx-xxxx con AR seleccionado)
 *   +54011...  → +54911...   (0 local removido)
 *   +54911...  → +54911...   (ya correcto, no se toca)
 *   +1555...   → +1555...    (otro país, no se toca)
 */
export function normalizeArgentinePhone(raw: string | undefined): string | undefined {
    if (!raw) return raw;
    if (!raw.startsWith('+54')) return raw;

    const digits = raw.slice(3); // todo después de +54
    // Ya tiene el 9 móvil → no tocar
    if (digits.startsWith('9')) return raw;
    // Quitar el 0 de cabecera local (015 → 15, 011 → 11)
    const stripped = digits.startsWith('0') ? digits.slice(1) : digits;
    return `+549${stripped}`;
}

export type PhoneInputComponentProps = React.ComponentProps<typeof PhoneInputWithCountrySelect>;

/**
 * Componente wrapper que aplica `normalizeArgentinePhone` al perder foco.
 * Acepta las mismas props que PhoneInputWithCountrySelect.
 */
const PhoneInput: React.FC<PhoneInputComponentProps> = ({ onChange, onBlur, value, ...rest }) => {
    const lastValueRef = useRef<string | undefined>(value as string | undefined);

    useEffect(() => {
        lastValueRef.current = value as string | undefined;
    }, [value]);

    const handleChange = useCallback(
        (value: string | undefined) => {
            lastValueRef.current = value;
            onChange?.(value as any);
        },
        [onChange],
    );

    const handleBlur = useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            const currentValue = lastValueRef.current;
            const normalized = normalizeArgentinePhone(currentValue);
            if (normalized !== currentValue) {
                lastValueRef.current = normalized;
                onChange?.(normalized as any);
            }
            onBlur?.(event);
        },
        [onBlur, onChange],
    );

    return <PhoneInputWithCountrySelect {...rest} value={value} onChange={handleChange} onBlur={handleBlur} />;
};

export default PhoneInput;
